import { useState, useRef, useEffect, useCallback } from "react";
import { Search, X, TrendingUp, Globe } from "lucide-react";
import { finnhubAPI } from "../services/api";

export const StockSearch = ({ value, onSelect, onSelectFull, placeholder, compact }) => {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync external value
  useEffect(() => { setQuery(value || ""); }, [value]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 1) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const data = await finnhubAPI.searchStocks(q);
      setResults(data);
      setOpen(data.length > 0);
      setActiveIdx(-1);
    } catch { setResults([]); }
    setLoading(false);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(v), 250);
  };

  const handleSelect = (item) => {
    setQuery(compact ? "" : item.symbol);
    setOpen(false);
    setResults([]);
    if (onSelectFull) onSelectFull(item);
    else if (onSelect) onSelect(item.symbol);
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) {
      if (e.key === "Enter") {
        if (onSelect) onSelect(query.toUpperCase());
        if (compact) setQuery("");
        return;
      }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0 && activeIdx < results.length) handleSelect(results[activeIdx]);
      else if (onSelect) onSelect(query.toUpperCase());
      setOpen(false);
      if (compact) setQuery("");
    }
    else if (e.key === "Escape") setOpen(false);
  };

  const exchangeFlag = (exch) => {
    const map = { "Paris": "\uD83C\uDDEB\uD83C\uDDF7", "Francfort": "\uD83C\uDDE9\uD83C\uDDEA", "Frankfurt": "\uD83C\uDDE9\uD83C\uDDEA", "London": "\uD83C\uDDEC\uD83C\uDDE7", "LSE": "\uD83C\uDDEC\uD83C\uDDE7", "Milan": "\uD83C\uDDEE\uD83C\uDDF9", "Amsterdam": "\uD83C\uDDF3\uD83C\uDDF1", "Madrid": "\uD83C\uDDEA\uD83C\uDDF8", "Toronto": "\uD83C\uDDE8\uD83C\uDDE6", "TSX": "\uD83C\uDDE8\uD83C\uDDE6", "NYSE": "\uD83C\uDDFA\uD83C\uDDF8", "NMS": "\uD83C\uDDFA\uD83C\uDDF8", "NASDAQ": "\uD83C\uDDFA\uD83C\uDDF8", "NGM": "\uD83C\uDDFA\uD83C\uDDF8", "NCM": "\uD83C\uDDFA\uD83C\uDDF8", "OTC Markets": "\uD83C\uDDFA\uD83C\uDDF8", "Tokyo": "\uD83C\uDDEF\uD83C\uDDF5", "Hong Kong": "\uD83C\uDDED\uD83C\uDDF0", "Shanghai": "\uD83C\uDDE8\uD83C\uDDF3", "Shenzhen": "\uD83C\uDDE8\uD83C\uDDF3", "Munich": "\uD83C\uDDE9\uD83C\uDDEA", "Stuttgart": "\uD83C\uDDE9\uD83C\uDDEA", "Vienna": "\uD83C\uDDE6\uD83C\uDDF9", "Brussels": "\uD83C\uDDE7\uD83C\uDDEA", "Lisbon": "\uD83C\uDDF5\uD83C\uDDF9", "BATS Europe": "\uD83C\uDDEA\uD83C\uDDFA", "DXE": "\uD83C\uDDEA\uD83C\uDDFA" };
    return map[exch] || "\uD83C\uDF10";
  };

  return (
    <div ref={wrapperRef} className={`relative ${compact ? "w-56" : "flex-1"}`}>
      {!compact && <label className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-2 block">Rechercher une action</label>}
      <div className="relative">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${compact ? "w-3.5 h-3.5" : "w-4 h-4"} text-slate-500`} />
        <input
          type="text"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setOpen(true)}
          className={`w-full bg-slate-800 border border-slate-600 rounded-lg text-white font-mono focus:border-cyan-500/60 focus:outline-none transition-colors ${
            compact ? "pl-8 pr-8 py-2 text-sm" : "pl-10 pr-10 py-3 text-lg"
          }`}
          placeholder={placeholder || "NVDA, Apple, Thales..."}
          data-testid={compact ? "watchlist-search-input" : "algo-search-input"}
        />
        {query && (
          <button onClick={() => { setQuery(""); setResults([]); setOpen(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors">
            <X className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />
          </button>
        )}
        {loading && (
          <div className={`absolute ${compact ? "right-8" : "right-10"} top-1/2 -translate-y-1/2`}>
            <div className="w-3.5 h-3.5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-xl shadow-2xl overflow-hidden max-h-80 overflow-y-auto"
          data-testid="search-results-dropdown">
          {results.map((item, i) => (
            <button
              key={`${item.symbol}-${i}`}
              onClick={() => handleSelect(item)}
              onMouseEnter={() => setActiveIdx(i)}
              className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-colors ${
                i === activeIdx ? "bg-cyan-500/15 border-l-2 border-cyan-400" : "hover:bg-slate-700/60 border-l-2 border-transparent"
              }`}
              data-testid={`search-result-${item.symbol}`}
            >
              <span className="text-lg">{exchangeFlag(item.exchange)}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-cyan-400 text-sm">{item.symbol}</span>
                  <span className="text-white text-sm truncate">{item.name}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-slate-500 flex items-center gap-1">
                    <Globe className="w-3 h-3" />{item.exchange}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 bg-slate-700 text-slate-400 rounded">{item.type}</span>
                </div>
              </div>
              <TrendingUp className="w-4 h-4 text-slate-600" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
